import { Temporal } from "@js-temporal/polyfill";
import { z } from "zod";
import { dayMs, hourMs, minuteMs, secondMs, weekMs } from "../../timeConstants.js";
import { type DurationField, type DurationUnit, durationFieldByUnit, parseDuration } from "./duration.js";

/** A validated bucket width with the execution fields derived from it. */
export type BucketWidth = {
    /** The caller's original spelling, echoed back on the response. */
    raw: string;
    /** The shared PostgreSQL-serializable Temporal duration. */
    duration: Temporal.Duration;
    /** How many of `unit` one bucket spans — 15 for `15m`. Not a count of buckets. */
    unitCount: number;
    unit: DurationUnit;
    /** Null for the calendar units (`mo`, `y`), whose width varies by position. */
    fixedWidthMs: number | null;
};

/** Aggregate functions available for populated time buckets. */
export const BucketAggregateSchema = z.enum(["mean", "median", "first", "last", "min", "max", "sum", "count"]);
/** An aggregate function available for populated time buckets. */
export type BucketAggregate = z.infer<typeof BucketAggregateSchema>;

/**
 * Aggregates a bucketed data-point read accepts: the {@link BucketAggregateSchema} functions plus
 * `timeWeightedMean`, where each sample holds until the next one, at most for the stream's gap limit.
 */
export const BucketedReadAggregateSchema = z.enum([...BucketAggregateSchema.options, "timeWeightedMean"]);
/** An aggregate a bucketed data-point read accepts. */
export type BucketedReadAggregate = z.infer<typeof BucketedReadAggregateSchema>;

/** Strategies for filling empty time buckets. */
export const GapfillModeSchema = z.enum(["null", "locf", "interpolate"]);
/** A strategy for filling empty time buckets. */
export type GapfillMode = z.infer<typeof GapfillModeSchema>;

/**
 * Units whose width is constant, so a bucket grid can be laid out by arithmetic on epoch
 * milliseconds. The units left out (`mo`, `y`) vary by calendar position and need calendar maths.
 */
const fixedWidthMsByUnit = { s: secondMs, m: minuteMs, h: hourMs, d: dayMs, w: weekMs } as const;

const durationUnitFields = Object.entries(durationFieldByUnit) as [DurationUnit, DurationField][];

/**
 * Largest accepted count per unit, all roughly a century. The grid maths in
 * `backend/src/common/services/dataStreamService/bucketing.ts` walks `Date`s, so an absurd width (`1000000y`) overflows the `Date`
 * range and yields `Invalid Date` rather than failing outright.
 */
const MAX_UNIT_COUNT_BY_UNIT: Record<DurationUnit, number> = {
    s: 100 * 365 * 24 * 60 * 60,
    m: 100 * 365 * 24 * 60,
    h: 100 * 365 * 24,
    d: 100 * 365,
    w: 100 * 52,
    mo: 100 * 12,
    y: 100,
};

/**
 * Maximum number of buckets a single gap-filled query may materialize. The API rejects a bucketed
 * request covering more than this, so callers chunk wider ranges rather than paginate.
 */
export const MAX_GAPFILL_BUCKETS = 5_000;

/**
 * A bucket width, parsed once at the request boundary into the fields the bucket grid and SQL both
 * need. Accepts compact Verdi syntax (`15m`) or ISO-8601 (`PT15M`), then restricts the result to
 * exactly one positive whole unit with no sub-second precision — the shape a repeating grid needs
 * to have a single, unambiguous cadence.
 *
 * The single-unit rule belongs to bucket widths, not to durations generally:
 * {@link parseDuration}'s ISO-8601 form otherwise allows freely combining units. Tracked for
 * relaxation to mixed units (e.g. `1mo3d`) in SWD-3106.
 */
export const BucketWidthSchema = z.string().transform((raw, context): BucketWidth => {
    const reject = (message: string) => {
        context.addIssue({ code: "custom", message });
        return z.NEVER;
    };

    let duration: Temporal.Duration;
    try {
        duration = parseDuration(raw);
    } catch (error) {
        return reject(error instanceof Error ? error.message : "Invalid bucketWidth");
    }

    const populated = durationUnitFields.filter(([, field]) => duration[field] !== 0);
    if (
        populated.length !== 1 ||
        duration.milliseconds !== 0 ||
        duration.microseconds !== 0 ||
        duration.nanoseconds !== 0
    ) {
        return reject("bucketWidth must contain exactly one positive whole duration unit");
    }

    const [unit, field] = populated[0];
    const unitCount = duration[field];
    if (!Number.isSafeInteger(unitCount) || unitCount <= 0) {
        return reject("bucketWidth unit count must be a positive safe integer");
    }
    // A width beyond this can overflow the grid arithmetic into `Invalid Date`, which would
    // otherwise reach PostgreSQL as a bind parameter and fail there instead of here. No real
    // query buckets by more than a century, so this only rejects nonsense input.
    if (unitCount > MAX_UNIT_COUNT_BY_UNIT[unit]) {
        return reject(`bucketWidth is too large; at most ${MAX_UNIT_COUNT_BY_UNIT[unit]}${unit} is supported`);
    }

    return {
        raw,
        duration,
        unitCount,
        unit,
        fixedWidthMs:
            unit in fixedWidthMsByUnit ? fixedWidthMsByUnit[unit as keyof typeof fixedWidthMsByUnit] * unitCount : null,
    };
});
